import { useEffect, useMemo } from 'react';
import { useGetList, useStore } from 'react-admin';
import { Box } from '@mui/material';
import { DataStats } from "../../models/data-stats";
import { GlobalMapFilter } from '../../components/global-map-filter/global-map-filter';
import { useDateRange } from '../../hooks/use-date-range';
import { useMapAttributeType } from '../../hooks/use-map-attribute';
import { NeighborhoodDataMap } from './components/neighborhood-data-map/neighborhood-data-map';
import { NeighborhoodDataDistribution, NeighborhoodDataDto } from '../../models/neighborhood-data';
import { RegionInfoPanel } from '../../components/region-info-panel/region-info-panel';
import { RegionInfo } from '@/models/region';

const styles = {
  leftCol: { display: 'flex', flexDirection: 'column', flex: 1 },
  rightCol: { flex: 5, marginLeft: '0.5em' },
};

export type DataFilter = {
  type: 'sale' | 'rent';
  from: string,
  to: string;
}

export const NeighborhoodMapPage = () => {
  const [fromDate, toDate] = useDateRange();
  const [mapAttributeType] = useMapAttributeType();

  // Reset region info when loading new page
  const [region, setRegion] = useStore<RegionInfo | undefined>('region.active', undefined);
  useEffect(() => {
    if (region) setRegion(undefined);
  }, []);

  const dataFilter = useMemo<DataFilter>(() => {
    return {
      type: mapAttributeType.toString(),
      from: fromDate,
      to: toDate
    } as DataFilter;
  }, [fromDate, toDate, mapAttributeType]);

  const { data, isLoading, isFetching } = useGetList<NeighborhoodDataDto>('data/neighborhood', {
    filter: {
      type: dataFilter.type,
      from: dataFilter.from,
      to: dataFilter.to
    },
  });

  // // TODO: Implement taxes data
  // const { data: taxesData, isLoading: isLoadingTaxes } = useGetList('taxes', {
  //   filter: {
  //     type: dataFilter.type,
  //     from: dataFilter.from,
  //     to: dataFilter.to
  //   },
  // }); 

  const dataDistribution = useMemo<NeighborhoodDataDistribution>(() => {
    if (!data) return {};
    const aggregations = data
      .reduce(
        (stats: DataStats, currentData) => {
          if (stats.dataMin > currentData.dataMean)
            stats.dataMin = currentData.dataMean;
          if (stats.dataMax < currentData.dataMean)
            stats.dataMax = currentData.dataMean;
          if (currentData.dataCount > stats.dataCountMax)
            stats.dataCountMax = currentData.dataCount;
          stats.dataMean += currentData.dataMean;
          return stats;
        },
        {
          dataMin: data[0]?.dataMean ?? 0,
          dataMax: 0,
          dataMean: 0,
          dataCountMax: 0,
        }
      );
    aggregations.dataMean /= data.length;
    return {
      data: data,
      stats: aggregations
    };
  }, [data]);

  return (
    <Box>
      <GlobalMapFilter dataStats={dataDistribution.stats!} mapDataType='neighborhood' />
      <Box sx={{ display: 'flex' }}>
        <Box sx={styles.leftCol}>
          <Box sx={{ borderTop: '1px solid white', padding: '1em', marginTop: '1em' }}>
            <RegionInfoPanel />
          </Box>
        </Box>
        <Box sx={styles.rightCol}>
          <NeighborhoodDataMap
            data={dataDistribution}
            isLoading={isLoading || isFetching}
          //taxes={mapAttributeType === 'AverageTaxRate' ? taxesData : []} 
          />
        </Box>
      </Box>
    </Box>
  );
};
